import React from 'react';
import { useRecoilValue } from 'recoil';
import axios from 'axios';
import styled from 'styled-components';

import useModal from '@/hooks/useModal';
import TitleNAdditionalContents from '@/components/ContextPrinter/TitleNAdditionalContents';
import QnAInfoByStatusDetail from '@/components/ContextPrinter/QnAInfoByStatusDetail';
import SvgSelector from '@/components/Svg/SvgSelector';
import VerticalImageNContent from '@/components/ContextPrinter/VerticalImageNContent';
import AccuseModal from '@/containers/modal/InA/AccuseModal';
import ReviseModal from '@/containers/modal/InA/ReviseModal';
import { loginState } from '@/recoil';
import { FontStyle } from '@/utils/StyleUtil';
import { CATEGORY_LIST, QNA_STATUS, QuestionDetail } from '@/models/ina';
import ImageCarouselDefault from '@/components/Viewer/ImageCarouselDefault';
import Seperator from '@/components/Spacing/Seperator';
import useQueryHook from '@/hooks/useQueryHook';
import { API, URLs } from '@/api';
import Image from 'next/future/image';
import StringUtil from '@/utils/StringUtil';
import Balloon from '@/components/CustomContainer/Balloon';
import { TIcons } from '@/components/Svg/SvgSelector';

interface IProps {
    questionId: number;
    questionData: QuestionDetail;
    files: string[];
    questionStatus: number;
}

const RenderQuestion = ({ questionId, questionData, files, questionStatus }: IProps) => {
    const { clientId } = useRecoilValue(loginState);
    const { addModal, removeCurrentModal } = useModal();

    let iconBgColor: string;
    let iconTextColor: string;
    let statusText: string;
    let svgFile: string;
    let statusBoxBorder: string;
    let left: number;

    switch (questionStatus) {
        case QNA_STATUS.WAITING:
            iconBgColor = '#FFE4E4';
            iconTextColor = '#FF1E1E';
            statusText = '답변대기';
            svgFile = 'ina-answer-waiting';
            statusBoxBorder = '#FDEAEA';
            left = 16;
            break;
        case QNA_STATUS.COMPLETE:
            iconBgColor = '#EAFAFF';
            iconTextColor = '#00ACE2';
            statusText = '답변완료';
            svgFile = 'ina-answer-complete';
            statusBoxBorder = '#E3F3F8';
            left = 16;
            break;
        case QNA_STATUS.UNANSWERED:
            iconBgColor = '#F0F0F0';
            iconTextColor = '#ACACAC';
            statusText = '미답변';
            svgFile = 'ina-answer-expired';
            statusBoxBorder = '#E4E4E4';
            left = 21;

            break;
        case QNA_STATUS.ANSWER:
            iconBgColor = '#eafffe';
            iconTextColor = '#0ec4cf';
            statusText = '';
            statusBoxBorder = '#FDEAEA';
            break;
    }

    const setCategory = () => {
        if (questionData.category) {
            const questionCategoryList = CATEGORY_LIST.filter((_: string, index: number) =>
                questionData.category.includes(index + 1),
            );

            return questionCategoryList;
        }

        return undefined;
    };

    const onClickMore = () => {
        if (questionData.clientId === Number(clientId)) {
            addModal({
                key: 'slide',
                props: {
                    content: (
                        <ReviseModal
                            questionId={questionId}
                            content={questionData.content}
                            category={questionData.category}
                            title={questionData.title}
                            fileNames={files}
                            fileURLs={saltArr}
                        />
                    ),
                    handleCloseModal: removeCurrentModal,
                },
            });
        } else {
            addModal({
                key: 'slide',
                props: {
                    content: <AccuseModal contentType={1} contentId={questionId} />,
                    handleCloseModal: removeCurrentModal,
                },
            });
        }
    };

    const { data: saltArr }: { data: string[] } = useQueryHook(
        ['get-images-salt', ...files],
        async () => {
            const apiList = files.map(file => API.get(URLs.download.inaFile(questionData.id, file)));
            const res = await axios.all(apiList);
            const imageArr: string[] = [];
            res.forEach(element => {
                {
                    const url = element as unknown as string;
                    imageArr.push(URLs.temp.getFileUrl(encodeURIComponent(url)));
                }
            });
            return imageArr;
        },
        {
            cacheTime: 0,
            staleTime: 0,
        },
    );

    const renderQuestion = () => {
        return (
            <QuestionContainer>
                <EtcContainer>
                    <FirstThreeItems>
                        <Image src={'/img/ina/curious.png'} width={22} height={22} alt="curious" />
                        <SvgIconAndText>
                            <SvgSelector svg={svgFile as TIcons} stroke={statusBoxBorder} width={66} height={26} />
                            <SvgText iconTextColor={iconTextColor} left={left}>
                                {statusText}
                            </SvgText>
                        </SvgIconAndText>
                        <CreatedAt>{StringUtil.displayedAt(questionData.createdAt)}</CreatedAt>
                    </FirstThreeItems>
                    <MoreContainer onClick={onClickMore}>
                        <SvgSelector svg="more-vert" width={20} height={20} stroke="#ACACAC" />
                    </MoreContainer>
                </EtcContainer>
                <TitleContainer>
                    <TitleNAdditionalContents
                        title={questionData.title.replace(/\r+|\n+/g, ' ')}
                        titleStyle={FontStyle(20, 30, '700', '#222', -1)}
                        tags={setCategory()}
                        className="renderQnATitle"
                    />
                </TitleContainer>
                <div style={{ height: '22px' }} />
                <QnAInfoByStatusDetail
                    // questionId={questionData.id}
                    status={questionData.status}
                    content={questionData.content}
                    answerCount={questionData.answerCount}
                    // createdAt={questionData.createdAt}
                    showStatus={false}
                />
                <Seperator height={22} />
                {saltArr?.length > 0 && <ImageCarousel imageArr={saltArr} />}
            </QuestionContainer>
        );
    };

    const renderBlinedQuestion = () => {
        return (
            <BlindedQuestionContainer>
                <VerticalImageNContent
                    svg="round-exclamation-mark-circle"
                    width={40}
                    height={40}
                    stroke="#c8c8c8"
                    strokeWidth={1.6}
                    content={`해당 질문은 관리자에 의해\n블라인드 처리된 글입니다.\n(자세한 내용은 앱 이용 관련 문의 바랍니다)`}
                    fontStyle={{ color: '#acacac' }}
                />
            </BlindedQuestionContainer>
        );
    };

    if (questionData.isBlind) {
        return renderBlinedQuestion();
    }
    return renderQuestion();
};

const QuestionContainer = styled.div`
    padding: 0 20px 30px 20px;
    background-color: #ffffff;
`;

const TitleContainer = styled.div`
    margin-top: 16px;
`;

const EtcContainer = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
    align-items: center;
    justify-content: space-between;
    margin-top: 22px;
`;

const FirstThreeItems = styled.div`
    ${({ theme }) => theme.flex.flexOnly};
    align-items: center;
    gap: 5px;
`;

const StatusContainer = styled.div<{ iconBgColor: string; iconTextColor: string }>`
    background-color: ${({ iconBgColor }) => iconBgColor};
    ${({ iconTextColor }) => FontStyle(12, 20, '500', iconTextColor, -1)};
    padding: 3px 8px;
    border-radius: 6px;
    border: 1px solid #e3f3f8;
`;

const SvgIconAndText = styled.div`
    position: relative;
    display: flex;
`;

const SvgText = styled.div<{ iconTextColor: string; left: number }>`
    position: absolute;
    ${({ iconTextColor }) => FontStyle(12, 20, '500', iconTextColor, -1)};
    top: 3px;
    left: ${({ left }) => left}px;
`;

const CreatedAt = styled.div`
    ${FontStyle(12, 20, '400', '#ACACAC', -1)};
`;

const MoreContainer = styled.div`
    height: 20px;
`;

const BlindedQuestionContainer = styled.div`
    padding: 54px 0 74px 0;
    background-color: #ffffff;
`;

const ImageCarousel = styled(ImageCarouselDefault)`
    padding: 0;
`;

const StatusBalloon = styled(Balloon)`
    margin-left: 7px;
`;

export default RenderQuestion;
